import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Tooltip,
  IconButton,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AuthContext from '../contexts/AuthContext';

export default function AllTransactions() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [selected, setSelected] = useState(null);
  const [highlightTxnId, setHighlightTxnId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const flashedRef = useRef(false);
  // Organization filter state
  const [orgInput, setOrgInput] = useState('');
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState(null); // 'paid' | 'unpaid' | null

  const shortId = (val) => {
    if (!val) return '';
    const s = String(val);
    return s.length > 8 ? `${s.slice(0, 8)}…` : s;
  };

  const paymentStatusFor = (inv) => {
    if (!inv) return '—';
    const balance = inv?.balance_amount != null ? Number(inv.balance_amount) : null;
    if (balance === 0) return 'Paid';
    return inv.payments?.length ? 'Partial' : 'Unpaid';
  };

  const formatTxnType = (t) => {
    const map = {
      kit_restock_qb_invoice: 'Kit Restock',
    };
    return map[t] || t;
  };

  const fetchData = async () => {
    if (!tokens?.id_token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('skip', String(page * rowsPerPage));
      params.append('limit', String(rowsPerPage));
      params.append('sort_by', orderBy);
      params.append('sort_order', order);
      if (selectedOrg?.id) params.append('organization_id', selectedOrg.id);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      const url = `${apiBase}/transactions?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tokens.id_token}` } });
      if (!res.ok) throw new Error('Failed to fetch transactions');
      const data = await res.json();
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message || 'Error fetching transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tokens, page, rowsPerPage, order, orderBy, selectedOrg, paymentFilter]);

  // Debounced organization search
  useEffect(() => {
    let active = true;
    const q = (orgInput || '').trim();
    if (!tokens?.id_token) return;
    if (q.length < 2) {
      setOrgOptions([]);
      return;
    }
    setOrgLoading(true);
    const handle = setTimeout(async () => {
      try {
        const url = `${apiBase}/organizations?search_text=${encodeURIComponent(q)}&limit=10`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${tokens.id_token}` } });
        if (!res.ok) throw new Error('Failed to search organizations');
        const data = await res.json();
        if (!active) return;
        const opts = (data.items || []).map(o => ({ id: o.id, name: o.name }));
        setOrgOptions(opts);
      } catch (e) {
        if (active) setOrgOptions([]);
      } finally {
        if (active) setOrgLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(handle); };
  }, [orgInput, tokens, apiBase]);

  // Optional: support highlight via query params if linked here
  useEffect(() => {
    if (flashedRef.current) return;
    if (!rows || rows.length === 0) return;
    const params = new URLSearchParams(location.search);
    const targetInvoiceId = params.get('invoice_id');
    const targetRealmId = params.get('realm_id');
    if (!targetInvoiceId || !targetRealmId) return;
    const match = rows.find(r => r.qb_invoice && r.qb_invoice.invoice_id === targetInvoiceId && r.qb_invoice.realm_id === targetRealmId);
    if (match) {
      setHighlightTxnId(match.id);
      flashedRef.current = true;
      // Clear the query params so it only flashes once
      navigate('/all-transactions', { replace: true });
      // Remove highlight after a short delay
      setTimeout(() => setHighlightTxnId(null), 2000);
    }
  }, [rows, location.search, navigate]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (loading) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress />
      </Container>
    );
  }
  if (error) {
    return (
      <Container disableGutters sx={{ mt: 2 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        All Transactions
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: { sm: 420, md: 560 } }}>
          <Autocomplete
            options={orgOptions}
            getOptionLabel={(opt) => opt?.name || ''}
            loading={orgLoading}
            value={selectedOrg}
            onChange={(e, val) => { setSelectedOrg(val); setPage(0); }}
            inputValue={orgInput}
            onInputChange={(e, val) => setOrgInput(val)}
            clearOnEscape
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by organization"
                placeholder="Type to search organizations"
                size="small"
                fullWidth
              />
            )}
          />
        </Box>
        <ToggleButtonGroup
          value={paymentFilter || 'all'}
          exclusive
          onChange={(e, val) => { setPaymentFilter(val === 'all' ? null : val); setPage(0); }}
          size="small"
          aria-label="payment status filter"
          sx={{ ml: { xs: 0, sm: 'auto' } }}
        >
          <ToggleButton value={'all'} aria-label="all">All</ToggleButton>
          <ToggleButton value="paid" aria-label="paid">Paid</ToggleButton>
          <ToggleButton value="unpaid" aria-label="unpaid">Unpaid</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell sortDirection={orderBy === 'created_at' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'desc'}
                  onClick={() => handleRequestSort('created_at')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell sortDirection={orderBy === 'total_items' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'total_items'}
                  direction={orderBy === 'total_items' ? order : 'desc'}
                  onClick={() => handleRequestSort('total_items')}
                >
                  Items
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'total_amount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'total_amount'}
                  direction={orderBy === 'total_amount' ? order : 'desc'}
                  onClick={() => handleRequestSort('total_amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Payment Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const inv = r.qb_invoice;
              const balance = inv?.balance_amount != null ? Number(inv.balance_amount) : null;
              const status = inv
                ? (balance === 0 ? 'Paid' : (inv.payments?.length ? 'Partial' : 'Unpaid'))
                : '—';
              return (
                <TableRow
                  key={r.id}
                  hover
                  onClick={() => setSelected(r)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: r.id === highlightTxnId ? '#fff9c4' : undefined,
                    transition: 'background-color 0.4s ease-in-out',
                  }}
                >
                  <TableCell>{r.organization_name || r.organization_id}</TableCell>
                  <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</TableCell>
                  <TableCell>{formatTxnType(r.transaction_type)}</TableCell>
                  <TableCell>{inv?.invoice_number || '—'}</TableCell>
                  <TableCell>{r.total_items ?? ''}</TableCell>
                  <TableCell>{r.total_amount != null ? `$${Number(r.total_amount).toFixed(2)}` : ''}</TableCell>
                  <TableCell>{balance != null ? `$${balance.toFixed(2)}` : '—'}</TableCell>
                  <TableCell>{status}</TableCell>
                </TableRow>
              );
            })}
      </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
      {/* Details Dialog */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              {/* Top card: transaction details */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Transaction</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <strong>ID:</strong> {shortId(selected.id)}
                      <Tooltip title="Copy full ID" arrow>
                        <IconButton
                          aria-label="copy transaction id"
                          size="small"
                          onClick={() => navigator.clipboard.writeText(selected.id)}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6}><strong>Type:</strong> {formatTxnType(selected.transaction_type)}</Grid>
                    <Grid item xs={12} sm={6}><strong>Organization:</strong> {selected.organization_name || selected.organization_id}</Grid>
                    <Grid item xs={12} sm={6}><strong>Created:</strong> {selected.created_at ? new Date(selected.created_at).toLocaleString() : ''}</Grid>
                    <Grid item xs={12} sm={6}><strong>Total Items:</strong> {selected.total_items ?? '—'}</Grid>
                    <Grid item xs={12} sm={6}><strong>Total Amount:</strong> {selected.total_amount != null ? `$${Number(selected.total_amount).toFixed(2)}` : '—'}</Grid>
                    <Grid item xs={12} sm={6}><strong>Payment Status:</strong> {paymentStatusFor(selected.qb_invoice)}</Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Bottom card: QB Invoice details */}
              <Card variant="outlined" sx={{ mb: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Invoice</Typography>
                  {selected.qb_invoice ? (
                    <Box>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}><strong>Invoice #:</strong> {selected.qb_invoice.invoice_number}</Grid>
                        <Grid item xs={12} sm={6}><strong>Invoice ID:</strong> {selected.qb_invoice.invoice_id}</Grid>
                        <Grid item xs={12} sm={6}><strong>Date:</strong> {selected.qb_invoice.invoice_created_time ? new Date(selected.qb_invoice.invoice_created_time).toLocaleString() : ''}</Grid>
                        <Grid item xs={12} sm={6}><strong>Currency:</strong> {selected.qb_invoice.currency_code || '—'}</Grid>
                        <Grid item xs={12} sm={4}><strong>Total:</strong> {selected.qb_invoice.total_amount != null ? `$${Number(selected.qb_invoice.total_amount).toFixed(2)}` : '—'}</Grid>
                        <Grid item xs={12} sm={4}><strong>Balance:</strong> {selected.qb_invoice.balance_amount != null ? `$${Number(selected.qb_invoice.balance_amount).toFixed(2)}` : '—'}</Grid>
                        <Grid item xs={12} sm={4}><strong>Status:</strong> {Number(selected.qb_invoice.balance_amount || 0) === 0 ? 'Paid' : (selected.qb_invoice.payments?.length ? 'Partial' : 'Unpaid')}</Grid>
                        <Grid item xs={12} sm={6}>
                          <strong>Discounts:</strong> {selected.qb_invoice.discount_amount != null ? `-$${Number(selected.qb_invoice.discount_amount).toFixed(2)}` : '—'}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <strong>Tax:</strong> {selected.qb_invoice.tax_amount != null ? `$${Number(selected.qb_invoice.tax_amount).toFixed(2)}` : '—'}
                        </Grid>
                      </Grid>
                      <Divider textAlign="left" sx={{ mb: 1 }}>Lines</Divider>
                      {selected.qb_invoice.lines && selected.qb_invoice.lines.length > 0 ? (
                        <Table size="small" sx={{ mb: 2 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Line #</TableCell>
                              <TableCell>Item</TableCell>
                              <TableCell>Quantity</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selected.qb_invoice.lines.map((ln) => (
                              <TableRow key={ln.id}>
                                <TableCell>{ln.line_number || ln.line_id}</TableCell>
                                <TableCell>{ln.item_name}</TableCell>
                                <TableCell>{ln.quantity ?? '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ mb: 2 }}>No line items available.</Typography>
                      )}
                      <Divider textAlign="left" sx={{ mb: 1 }}>Payments</Divider>
                      {selected.qb_invoice.payments && selected.qb_invoice.payments.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Payment ID</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selected.qb_invoice.payments.map((p) => (
                              <TableRow key={p.id}>
                                <TableCell>{p.payment_id}</TableCell>
                                <TableCell>{p.payment_create_time ? new Date(p.payment_create_time).toLocaleDateString() : ''}</TableCell>
                                <TableCell align="right">{p.amount != null ? `$${Number(p.amount).toFixed(2)}` : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2">No payments recorded.</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2">No invoice associated with this transaction.</Typography>
                  )}
                </CardContent>
              </Card>

              <Box sx={{ textAlign: 'right' }}>
                <Button onClick={() => setSelected(null)} variant="contained">Close</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
